import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUser } from '@/contexts/UserContext';
import {
  Globe,
  UserCircle,
  LogOut,
  User,
  LayoutDashboard,
  MessageCircle
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface PublicHeaderProps {
  onContactClick?: () => void;
}

export function PublicHeader({ onContactClick }: PublicHeaderProps = {}) {
  const { language, setLanguage, t } = useLanguage();
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hi, I'd like to inquire about your sourcing and shipping services.");
    window.open(`https://wa.me/8613048064436?text=${message}`, '_blank');
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-85 transition-opacity">
            <img
              src="/logo-full.jpg"
              alt="SourceMyGoods"
              className="h-16 w-auto object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling!.style.display = 'flex';
              }}
            />
            <span className="hidden items-center text-2xl font-bold" style={{ color: '#1e3a6e' }}>
              SourceMyGoods
            </span>
          </Link>

          {/* Navigation */}
          <div className="flex items-center gap-3">
            {/* Show Login/Register when NOT logged in */}
            {!user && (
              <nav className="hidden md:flex items-center gap-6">
                <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  {t('login')}
                </Link>
                <Link to="/register" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  {t('register')}
                </Link>
              </nav>
            )}

            {/* Show User Dropdown when logged in */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <UserCircle className="h-5 w-5 text-gray-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user.username}</p>
                      <p className="text-xs text-gray-500">
                        {user.role === 'admin' ? t('admin') : user.warehouseName}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    {t('dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="h-4 w-4 mr-2" />
                    {t('profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Language Selector - Compact */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
              className="flex items-center gap-1"
            >
              <Globe className="h-4 w-4" />
              <span className="text-xs font-medium">{language.toUpperCase()}</span>
            </Button>

            {/* Mobile Login Button (only when not logged in) */}
            {!user && (
              <Button variant="ghost" size="sm" className="md:hidden" onClick={() => navigate('/login')}>
                {t('login')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
